import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/fmp.css';

const formatarLabel = (campo) =>
  campo.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatarValor = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '-';
  if (typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}/.test(valor)) {
    const d = new Date(valor.length > 10 ? valor : `${valor}T00:00:00`);
    if (!isNaN(d.getTime())) {
      return valor.length > 10 ? d.toLocaleString('pt-BR') : d.toLocaleDateString('pt-BR');
    }
  }
  return String(valor);
};

// Datas ISO exibidas sempre como dd/mm/aaaa (sem hora)
const formatarData = (iso) => {
  if (!iso) return '-';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('pt-BR');
};

const rotuloMes = (iso) => {
  const d = new Date(`${iso.slice(0, 7)}-01T00:00:00`);
  const txt = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return txt.charAt(0).toUpperCase() + txt.slice(1);
};

const PERIODOS = [
  { meses: 0, label: 'Tudo' },
  { meses: 12, label: 'Últimos 12 meses' },
  { meses: 6, label: 'Últimos 6 meses' },
  { meses: 3, label: 'Últimos 3 meses' },
  { meses: 1, label: 'Último mês' },
];

const CARDS_RESUMO = [
  { campo: 'total_entregas', label: 'Entregas Contábeis', icone: 'fa-check-circle', cor: 'success' },
  { campo: 'pendencias_abertas', label: 'Pendências abertas', icone: 'fa-hourglass-half', cor: 'warning' },
  { campo: 'status_contabil', label: 'Situação contábil', icone: 'fa-clock', cor: 'dark' },
  { campo: 'ultima_auditoria', label: 'Última auditoria', icone: 'fa-search', cor: 'info' },
  { campo: 'ultimo_sped', label: 'Último SPED', icone: 'fa-file-alt', cor: 'primary' },
  { campo: 'ultimo_checklist', label: 'Último checklist', icone: 'fa-envelope', cor: 'info' },
  { campo: 'total_reunioes', label: 'Reuniões', icone: 'fa-video', cor: 'dark' },
  { campo: 'total_dividas_fiscais', label: 'Dívidas em aberto', icone: 'fa-file-invoice-dollar', cor: 'danger' },
  { campo: 'total_solicitacoes', label: 'Solicitações DP', icone: 'fa-inbox', cor: 'primary' },
  { campo: 'acessorias_situacao', label: 'Situação (Acessórias)', icone: 'fa-circle-check', cor: 'success' },
  { campo: 'acessorias_entregas', label: 'Entregas (Acessórias)', icone: 'fa-file-import', cor: 'primary' },
  { campo: 'acessorias_atrasadas', label: 'Atrasadas (Acessórias)', icone: 'fa-triangle-exclamation', cor: 'danger' },
  { campo: 'acessorias_pendentes', label: 'Pendentes (Acessórias)', icone: 'fa-hourglass-half', cor: 'warning' },
];

// Cor do valor do card de situação contábil conforme o texto.
const corStatusContabil = (valor) => {
  const s = String(valor || '').toLowerCase();
  if (s.includes('atrasad')) return 'danger';
  if (s.includes('em dia')) return 'success';
  return 'dark';
};

// Cor única para todos os ícones da página (azul-marinho da marca)
const COR_ICONE = '#00205b';

const ICONES_SECAO = {
  entregas: 'fa-check-circle',
  atrasos: 'fa-clock',
  auditorias: 'fa-search',
  checklist: 'fa-envelope',
  sped: 'fa-file-alt',
  relatorios: 'fa-paper-plane',
  reunioes_contabil: 'fa-video',
  reunioes_cs: 'fa-headset',
  analises_ia: 'fa-robot',
  impostos_recuperar: 'fa-coins',
  impostos_recuperar_hist: 'fa-history',
  devedores: 'fa-exclamation-triangle',
  anotacoes: 'fa-sticky-note',
  tempo: 'fa-stopwatch',
  fiscal_dividas: 'fa-file-invoice-dollar',
  fiscal_certidoes: 'fa-stamp',
  fiscal_dctfweb: 'fa-file-signature',
  fgts_guias: 'fa-money-check-alt',
  fgts_guias_novas: 'fa-money-check-alt',
  dp_guias: 'fa-receipt',
  dp_solicitacoes: 'fa-inbox',
  dp_funcionarios: 'fa-users',
  dp_ferias: 'fa-umbrella-beach',
  dp_afastamentos: 'fa-user-clock',
  dp_rescisoes: 'fa-user-slash',
  dp_rubricas: 'fa-list-ol',
  acessorias_entregas: 'fa-file-import',
  acessorias_boletos: 'fa-barcode',
};

const PortalCliente = () => {
  // Rota nova: /portal-cliente/:tipo/:valor  ·  legado: /portal-cliente/:numero_dominio
  const { tipo, valor, numero_dominio } = useParams();
  const navigate = useNavigate();

  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [aba, setAba] = useState('areas');
  const [periodoMeses, setPeriodoMeses] = useState(0);
  const [secoesAbertas, setSecoesAbertas] = useState({});
  const [mostrarVazias, setMostrarVazias] = useState(false);
  const [detalhe, setDetalhe] = useState(null); // { titulo, registro } da linha clicada
  const [cardModal, setCardModal] = useState(null); // { titulo, registros } do card clicado

  // URL do endpoint conforme o tipo de abertura (domínio, cnpj ou nome)
  const fetchInfo = useMemo(() => {
    const base = process.env.REACT_APP_API_BASE;
    if (tipo && valor) {
      return { url: `${base}/api/portal-por/${tipo}/${encodeURIComponent(valor)}/`, chave: `${tipo}:${valor}` };
    }
    if (numero_dominio) {
      return { url: `${base}/api/portal-cliente/${numero_dominio}/`, chave: `dominio:${numero_dominio}` };
    }
    return null;
  }, [tipo, valor, numero_dominio]);

  // Busca unificada (todo o banco) com debounce
  useEffect(() => {
    const termo = busca.trim();
    if (termo.length < 2) {
      setResultados([]);
      setBuscando(false);
      return;
    }
    setBuscando(true);
    const accessToken = localStorage.getItem('access');
    const t = setTimeout(() => {
      axios
        .get(`${process.env.REACT_APP_API_BASE}/api/portal-busca/`, {
          params: { q: termo },
          headers: { Authorization: `Bearer ${accessToken}` },
        })
        .then((res) => {
          setResultados(res.data.resultados || []);
          setBuscando(false);
        })
        .catch(() => {
          setResultados([]);
          setBuscando(false);
        });
    }, 300);
    return () => clearTimeout(t);
  }, [busca]);

  // Dados do portal quando muda o cliente alvo
  useEffect(() => {
    if (!fetchInfo) {
      setDados(null);
      return;
    }
    setCarregando(true);
    setErro('');
    setDados(null);
    setSecoesAbertas({});
    setMostrarVazias(false);
    const accessToken = localStorage.getItem('access');
    axios
      .get(fetchInfo.url, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        setDados(res.data);
        setCarregando(false);
      })
      .catch((error) => {
        const msg =
          error.response?.status === 404
            ? 'Empresa não encontrada.'
            : error.response?.data?.erro || error.message || 'Erro ao carregar dados';
        setErro(msg);
        setCarregando(false);
      });
  }, [fetchInfo]);

  const corteIso = useMemo(() => {
    if (!periodoMeses) return null;
    const d = new Date();
    d.setMonth(d.getMonth() - periodoMeses);
    return d.toISOString().slice(0, 19);
  }, [periodoMeses]);

  // Registros sem data parseável (_data nulo) sempre aparecem.
  const filtrarRegistros = (registros) => {
    if (!corteIso) return registros;
    return registros.filter((r) => !r._data || r._data >= corteIso);
  };

  // Limite superior: hoje (fim do dia). A timeline mostra só o que já
  // aconteceu — nada de prazos futuros/não feitos.
  const hojeFimIso = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T23:59:59`;
  }, []);

  const timelineFiltrada = useMemo(() => {
    if (!dados) return [];
    return dados.timeline.filter(
      (e) => e.data <= hojeFimIso && (!corteIso || e.data >= corteIso)
    );
  }, [dados, corteIso, hojeFimIso]);

  // Timeline agrupada por mês (eventos já vêm ordenados desc do backend)
  const timelinePorMes = useMemo(() => {
    const grupos = [];
    timelineFiltrada.forEach((e) => {
      const mes = e.data.slice(0, 7);
      const ultimo = grupos[grupos.length - 1];
      if (ultimo && ultimo.mes === mes) ultimo.eventos.push(e);
      else grupos.push({ mes, rotulo: rotuloMes(e.data), eventos: [e] });
    });
    return grupos;
  }, [timelineFiltrada]);

  // Separa seções com conteúdo (registros no período ou erro) das vazias
  const { secoesComDados, secoesVazias } = useMemo(() => {
    if (!dados) return { secoesComDados: [], secoesVazias: [] };
    const comDados = [];
    const vazias = [];
    Object.entries(dados.secoes).forEach(([chave, secao]) => {
      const registros = filtrarRegistros(secao.registros);
      const item = [chave, secao, registros];
      if (registros.length > 0 || secao.erro) comDados.push(item);
      else vazias.push(item);
    });
    return { secoesComDados: comDados, secoesVazias: vazias };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados, corteIso]);

  // Todas as entregas da Acessórias (de todos os tópicos), com o nome da obrigação.
  const acessoriasEntregasTodas = useMemo(() => {
    if (!dados) return [];
    const out = [];
    Object.entries(dados.secoes).forEach(([chave, secao]) => {
      if (chave.startsWith('acessorias_') && chave !== 'acessorias_boletos') {
        (secao.registros || []).forEach((r) => out.push({ obrigacao: secao.titulo, ...r }));
      }
    });
    return out;
  }, [dados]);

  const toggleSecao = (chave) =>
    setSecoesAbertas((prev) => ({ ...prev, [chave]: !prev[chave] }));

  // Filtros que espelham a lógica do backend (para casar com a contagem do card)
  const _dividaAtiva = (r) => {
    const saldo = parseFloat(r.saldo_devedor || 0) || 0;
    const sit = (r.situacao || '').toUpperCase();
    return saldo > 0 && !sit.includes('SEM DIVIDA') && !sit.includes('VENCER');
  };
  const _ehAtraso = (r) => (r.status || '').toLowerCase().includes('atras');
  const _ehPendente = (r) => {
    const low = (r.status || '').toLowerCase();
    const entregue = low.startsWith('ent.') || low.startsWith('ent ');
    return !entregue && (low.includes('pendente') || low.includes('prazo'));
  };

  // Monta o conteúdo do modal de um card de resumo.
  const abrirCard = (campo) => {
    if (!dados) return;
    const sec = (k) => dados.secoes?.[k]?.registros || [];
    const id = dados.identidade || {};
    let m = null;
    switch (campo) {
      case 'total_entregas':
        m = { titulo: 'Entregas Contábeis', registros: sec('entregas') }; break;
      case 'pendencias_abertas':
        m = { titulo: 'Pendências abertas', registros: sec('entregas').filter(
          (r) => ['parcial', 'atrasada e parcial'].includes((r.tipo_entrega || '').toLowerCase())
                 && (r.resolvido || 'não').toLowerCase() !== 'sim') }; break;
      case 'status_contabil':
        m = { titulo: 'Observações de Atraso', registros: sec('atrasos') }; break;
      case 'ultima_auditoria': {
        let regs = sec('auditorias');
        if (regs.length === 0 && dados.resumo.ultima_auditoria) {
          regs = [{ ultima_auditoria: dados.resumo.ultima_auditoria, origem: 'Cadastro contábil (sem histórico detalhado de auditoria)' }];
        }
        m = { titulo: 'Auditorias de Balancete', registros: regs }; break;
      }
      case 'ultimo_sped': {
        let regs = sec('sped');
        if (regs.length === 0 && dados.resumo.ultimo_sped) {
          regs = [{ ultimo_sped: dados.resumo.ultimo_sped, origem: 'Cadastro contábil (sem histórico detalhado de SPED)' }];
        }
        m = { titulo: 'SPED', registros: regs }; break;
      }
      case 'ultimo_checklist':
        m = { titulo: 'Checklists Enviados', registros: sec('checklist') }; break;
      case 'total_reunioes':
        m = { titulo: 'Reuniões', registros: [...sec('reunioes_contabil'), ...sec('reunioes_cs')] }; break;
      case 'total_dividas_fiscais':
        m = { titulo: 'Dívidas em aberto', registros: sec('fiscal_dividas').filter(_dividaAtiva) }; break;
      case 'total_solicitacoes':
        m = { titulo: 'Solicitações', registros: sec('dp_solicitacoes') }; break;
      case 'acessorias_entregas':
        m = { titulo: 'Entregas (Acessórias)', registros: acessoriasEntregasTodas }; break;
      case 'acessorias_atrasadas':
        m = { titulo: 'Atrasadas (Acessórias)', registros: acessoriasEntregasTodas.filter(_ehAtraso) }; break;
      case 'acessorias_pendentes':
        m = { titulo: 'Pendentes (Acessórias)', registros: acessoriasEntregasTodas.filter(_ehPendente) }; break;
      case 'acessorias_situacao':
        m = { titulo: 'Situação (Acessórias)', registros: id.acessorias ? [id.acessorias] : [] }; break;
      default:
        m = null;
    }
    if (m) setCardModal(m);
  };

  const renderTabela = (registros, tituloSecao) => {
    if (registros.length === 0) return null;
    const colunas = Object.keys(registros[0]).filter((c) => !c.startsWith('_'));
    return (
      <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
        <table className="table table-sm table-striped table-hover mb-0">
          <thead className="table-light sticky-top">
            <tr>
              {colunas.map((c) => (
                <th key={c} style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                  {formatarLabel(c)}
                </th>
              ))}
              <th style={{ width: '34px' }}></th>
            </tr>
          </thead>
          <tbody>
            {registros.map((r, i) => (
              <tr
                key={i}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                title="Ver detalhes deste registro"
                onClick={() => setDetalhe({ titulo: tituloSecao, registro: r })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDetalhe({ titulo: tituloSecao, registro: r });
                  }
                }}
              >
                {colunas.map((c) => (
                  <td key={c} style={{ fontSize: '0.82rem', maxWidth: '320px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {formatarValor(r[c])}
                  </td>
                ))}
                <td className="text-center text-muted">
                  <i className="fas fa-chevron-right" style={{ fontSize: '0.7rem' }}></i>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSecao = ([chave, secao, registros]) => {
    const aberta = !!secoesAbertas[chave];
    return (
      <div className="card border-0 shadow-sm mb-2" key={chave}>
        <div
          className="card-header bg-white d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer' }}
          onClick={() => toggleSecao(chave)}
        >
          <h6 className="card-title mb-0">
            <i className={`fas ${aberta ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`} style={{ color: COR_ICONE }}></i>
            <i className={`fas ${ICONES_SECAO[chave] || (chave.startsWith('acessorias') ? 'fa-file-import' : 'fa-folder')} me-2`} style={{ color: COR_ICONE }}></i>
            {secao.titulo}
            <span className={`badge ms-2 ${secao.erro ? 'bg-danger' : registros.length > 0 ? 'bg-primary' : 'bg-secondary'}`}>
              {secao.erro ? 'erro' : registros.length}
            </span>
            <span className="badge bg-light text-muted ms-1" style={{ fontSize: '0.65rem' }}>
              via {secao.cruzamento}
            </span>
          </h6>
          {secao.aviso && !secao.erro && (
            <span className="badge bg-warning text-dark" title={secao.aviso}>{secao.aviso}</span>
          )}
        </div>
        {aberta && (
          <div className="card-body p-2">
            {secao.erro && (
              <div className="alert alert-danger py-2 mb-2" style={{ fontSize: '0.8rem' }}>
                <i className="fas fa-exclamation-triangle me-1"></i>{secao.erro}
              </div>
            )}
            {registros.length > 0
              ? renderTabela(registros, secao.titulo)
              : !secao.erro && (
                  <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-inbox me-2" style={{ color: COR_ICONE }}></i>
                    {secao.aviso || 'Nenhum registro no período selecionado.'}
                  </p>
                )}
          </div>
        )}
      </div>
    );
  };

  const cliente = dados?.cliente;
  const identidade = dados?.identidade;
  const statusAtrasado =
    dados && String(dados.resumo.status_contabil || '').toLowerCase().includes('atrasado');
  const cnpjFormatado = identidade?.cnpj_digits
    ? identidade.cnpj_digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
    : '-';

  return (
    <div className="das-page">
      <div className="container-fluid mt-4">

        {/* HEADER */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-black">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                  {/* Lado Esquerdo - Info Cliente */}
                  <div className="flex-grow-1">
                    <div className="mb-4">
                      <span className="badge bg-light text-dark me-2" style={{ fontSize: '20px', fontWeight: '500' }}>
                        Visão 360º do Cliente
                      </span>
                      {dados && dados.resumo.status_contabil && (
                        <span className={`badge ${statusAtrasado ? 'bg-danger' : 'bg-success'}`} style={{ fontSize: '15px', fontWeight: '500' }}>
                          {dados.resumo.status_contabil}
                        </span>
                      )}
                    </div>

                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-address-card header-icon me-2"></i>
                      <h2 className="titulo-pagina m-0">
                        Portal do Cliente{cliente ? ` - ${cliente.empresa}` : ''}
                      </h2>
                    </div>

                    {cliente ? (
                      <>
                        <p className="mb-1 opacity-75" style={{ color: '#000' }}>
                          <i className="fas fa-barcode me-2" style={{ color: COR_ICONE }}></i>
                          Domínio: {cliente.numero_dominio}
                          <span className="ms-3">
                            <i className="fas fa-building me-2" style={{ color: COR_ICONE }}></i>
                            CNPJ: {cnpjFormatado}
                          </span>
                          {cliente.regime && (
                            <span className="ms-3">
                              <i className="fas fa-balance-scale me-2" style={{ color: COR_ICONE }}></i>
                              Regime: {cliente.regime}
                            </span>
                          )}
                        </p>
                        <p className="mb-0 opacity-75" style={{ color: '#000' }}>
                          <i className="fas fa-user-tie me-2" style={{ color: COR_ICONE }}></i>
                          Operador: {cliente.operador || '-'}
                          {cliente.grupo && (
                            <span className="ms-3">
                              <i className="fas fa-layer-group me-2" style={{ color: COR_ICONE }}></i>
                              Grupo: {cliente.grupo}
                            </span>
                          )}
                        </p>
                      </>
                    ) : (
                      <p className="mb-0 opacity-75" style={{ color: '#000' }}>
                        <i className="fas fa-info-circle me-2" style={{ color: COR_ICONE }}></i>
                        Busque uma empresa por nome, CNPJ ou número de domínio — em todo o banco — para ver o histórico completo.
                      </p>
                    )}
                  </div>

                  {/* Lado Direito - Busca + Ações */}
                  <div style={{ minWidth: '320px' }}>
                    <div style={{ position: 'relative' }}>
                      <div className="input-group">
                        <span className="input-group-text">
                          {buscando
                            ? <span className="spinner-border spinner-border-sm" role="status" style={{ color: COR_ICONE }}></span>
                            : <i className="fas fa-search" style={{ color: COR_ICONE }}></i>}
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Buscar por nome, CNPJ ou nº domínio..."
                          value={busca}
                          onChange={(e) => setBusca(e.target.value)}
                          onBlur={() => setTimeout(() => setBusca(''), 200)}
                        />
                      </div>
                      {busca.trim().length >= 2 && (
                        <div
                          className="list-group shadow"
                          style={{ position: 'absolute', zIndex: 1050, width: '100%', maxHeight: '340px', overflowY: 'auto' }}
                        >
                          {resultados.length === 0 && !buscando && (
                            <span className="list-group-item text-muted small">
                              <i className="fas fa-inbox me-2" style={{ color: COR_ICONE }}></i>
                              Nenhuma empresa encontrada
                            </span>
                          )}
                          {resultados.map((r) => (
                            <button
                              key={`${r.tipo}:${r.valor}`}
                              type="button"
                              className="list-group-item list-group-item-action text-start"
                              onMouseDown={(ev) => ev.preventDefault()}
                              onClick={() => {
                                setBusca('');
                                setResultados([]);
                                navigate(`/portal-cliente/${r.tipo}/${encodeURIComponent(r.valor)}`);
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center gap-2">
                                <strong className="text-truncate">{r.label}</strong>
                                <span className="d-flex gap-1 flex-shrink-0">
                                  {(r.fontes || []).map((f) => (
                                    <span key={f} className="badge bg-light text-muted text-uppercase" style={{ fontSize: '0.6rem' }}>{f}</span>
                                  ))}
                                </span>
                              </div>
                              <small className="text-muted">{r.sub}</small>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {cliente && (
                      <div className="d-flex gap-2 mt-2 justify-content-end flex-wrap">
                        {cliente.drive_cliente && cliente.drive_cliente !== '-' && (
                          <button
                            className="btn btn-sm"
                            onClick={() => window.open(cliente.drive_cliente, '_blank')}
                            title="Abrir Drive do cliente"
                            style={{ backgroundColor: '#00205b', borderColor: '#00205b', color: '#fff', fontSize: '0.72rem', padding: '3px 8px' }}
                          >
                            <i className="fas fa-external-link-alt me-1"></i>Drive
                          </button>
                        )}
                        {identidade?.cliente_bpo_id && (
                          <button
                            className="btn btn-sm"
                            onClick={() => navigate(`/dashboard-cliente/${identidade.cliente_bpo_id}`)}
                            title="Abrir painel BPO"
                            style={{ backgroundColor: '#00205b', borderColor: '#00205b', color: '#fff', fontSize: '0.72rem', padding: '3px 8px' }}
                          >
                            <i className="fas fa-chart-line me-1"></i>Painel BPO
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {identidade && identidade.nomes_usados.length > 0 && (
                  <p className="mb-0 mt-2 opacity-75" style={{ color: '#000', fontSize: '0.75rem' }}>
                    <i className="fas fa-link me-1" style={{ color: COR_ICONE }}></i>
                    Correspondência por nome: {identidade.nomes_usados.join(' · ')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* MENSAGENS DE ERRO */}
        {erro && !carregando && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="alert alert-danger alert-dismissible fade show">
                <i className="fas fa-exclamation-circle me-2"></i>{erro}
                <button type="button" className="btn-close" onClick={() => setErro('')}></button>
              </div>
            </div>
          </div>
        )}

        {/* LOADING */}
        {carregando && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p className="mt-3 text-muted">Carregando histórico do cliente...</p>
          </div>
        )}

        {dados && !carregando && cliente && (
          <>
            {/* FILTROS */}
            <div className="row mb-4">
              <div className="col-md-3">
                <label className="form-label fw-bold">
                  <i className="fas fa-filter me-2" style={{ color: COR_ICONE }}></i>
                  Período
                </label>
                <select
                  className="form-select"
                  value={periodoMeses}
                  onChange={(e) => setPeriodoMeses(Number(e.target.value))}
                >
                  {PERIODOS.map((p) => (
                    <option key={p.meses} value={p.meses}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-5 d-flex align-items-end gap-2">
                <button
                  className={`btn ${aba === 'areas' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setAba('areas')}
                >
                  <i className="fas fa-th-large me-2"></i>
                  Por Área
                </button>
                <button
                  className={`btn ${aba === 'timeline' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setAba('timeline')}
                >
                  <i className="fas fa-stream me-2"></i>
                  Linha do Tempo
                </button>
              </div>
            </div>

            {/* CARDS RESUMO */}
            <div className="row mb-4">
              {CARDS_RESUMO.map((c) => {
                const cor = c.campo === 'status_contabil'
                  ? corStatusContabil(dados.resumo[c.campo])
                  : c.cor;
                return (
                  <div className="col-xl-3 col-lg-4 col-md-6 mb-3" key={c.campo}>
                    <div
                      className="card border-0 shadow-sm h-100 pc-card-clicavel"
                      role="button"
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                      title="Clique para ver os detalhes"
                      onClick={() => abrirCard(c.campo)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          abrirCard(c.campo);
                        }
                      }}
                    >
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-2">
                          <div className="me-2" style={{ color: COR_ICONE }}>
                            <i className={`fas ${c.icone} fa-2x`}></i>
                          </div>
                          <div>
                            <h6 className="text-muted mb-0">{c.label}</h6>
                          </div>
                        </div>
                        <h4 className={`text-${cor} mb-0`} style={{ fontSize: '1.15rem' }}>
                          {formatarValor(dados.resumo[c.campo])}
                        </h4>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ABA: POR ÁREA */}
            {aba === 'areas' && (
              <div className="row mb-4">
                <div className="col-12">
                  {secoesComDados.map(renderSecao)}
                  {secoesComDados.length === 0 && (
                    <div className="card border-0 shadow-sm">
                      <div className="card-body text-center text-muted">
                        Nenhuma seção com registros no período selecionado.
                      </div>
                    </div>
                  )}
                  {secoesVazias.length > 0 && (
                    <>
                      <button
                        className="btn btn-light w-100 btn-sm text-muted mb-2"
                        onClick={() => setMostrarVazias((v) => !v)}
                      >
                        <i className={`fas fa-chevron-${mostrarVazias ? 'up' : 'down'} me-2`} style={{ color: COR_ICONE }}></i>
                        {mostrarVazias ? 'Ocultar' : 'Mostrar'} {secoesVazias.length} seções sem registros
                      </button>
                      {mostrarVazias && secoesVazias.map(renderSecao)}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ABA: LINHA DO TEMPO */}
            {aba === 'timeline' && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white">
                      <h6 className="card-title mb-0">
                        <i className="fas fa-stream me-2" style={{ color: COR_ICONE }}></i>
                        Linha do Tempo
                        <span className="badge bg-primary ms-2">{timelineFiltrada.length}</span>
                      </h6>
                    </div>
                    <div className="card-body">
                      {timelinePorMes.length === 0 ? (
                        <p className="text-muted text-center py-4 mb-0">
                          <i className="fas fa-inbox me-2" style={{ color: COR_ICONE }}></i>Nenhum evento no período.
                        </p>
                      ) : (
                        timelinePorMes.map((grupo) => (
                          <div key={grupo.mes} className="mb-3">
                            <h6 className="text-muted border-bottom pb-1 mb-2" style={{ fontSize: '0.8rem' }}>
                              <i className="fas fa-calendar me-2" style={{ color: COR_ICONE }}></i>
                              {grupo.rotulo}
                            </h6>
                            <ul className="list-group list-group-flush">
                              {grupo.eventos.map((e, i) => (
                                <li className="list-group-item d-flex align-items-center gap-3 px-0" key={i}>
                                  <span className="text-muted" style={{ fontSize: '0.8rem', minWidth: '75px' }}>
                                    {formatarData(e.data)}
                                  </span>
                                  <span className={`badge bg-${e.badge}`} style={{ minWidth: '150px' }}>
                                    {e.area_titulo}
                                  </span>
                                  <span style={{ fontSize: '0.9rem' }}>{e.titulo}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal do card de resumo (lista por trás do número) */}
      {cardModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setCardModal(null)}
        >
          <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                  <i className="fas fa-layer-group me-2"></i>
                  {cardModal.titulo}
                  <span className="badge bg-light text-dark ms-2">{cardModal.registros.length}</span>
                </h5>
                <button type="button" className="btn-close" onClick={() => setCardModal(null)}></button>
              </div>
              <div className="modal-body p-2">
                {cardModal.registros.length > 0 ? (
                  renderTabela(cardModal.registros, cardModal.titulo)
                ) : (
                  <p className="text-muted text-center py-4 mb-0">
                    <i className="fas fa-inbox me-2" style={{ color: COR_ICONE }}></i>
                    Nenhum registro para este item.
                  </p>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setCardModal(null)}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalhe da linha clicada */}
      {detalhe && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setDetalhe(null)}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                  <i className="fas fa-file-lines me-2"></i>
                  {detalhe.titulo}
                </h5>
                <button type="button" className="btn-close" onClick={() => setDetalhe(null)}></button>
              </div>
              <div className="modal-body p-0">
                <table className="table table-striped mb-0">
                  <tbody>
                    {Object.entries(detalhe.registro)
                      .filter(([k]) => !k.startsWith('_'))
                      .map(([k, v]) => {
                        const texto = formatarValor(v);
                        const ehLink = typeof v === 'string' && /^https?:\/\//i.test(v);
                        return (
                          <tr key={k}>
                            <th
                              className="text-muted"
                              style={{ width: '38%', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.02em', verticalAlign: 'top' }}
                            >
                              {formatarLabel(k)}
                            </th>
                            <td style={{ fontSize: '0.9rem', wordBreak: 'break-word' }}>
                              {ehLink ? (
                                <a href={v} target="_blank" rel="noreferrer">
                                  <i className="fas fa-external-link-alt me-1"></i>Abrir
                                </a>
                              ) : (
                                texto
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setDetalhe(null)}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalCliente;
